import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { GroupVisitControllerApi } from "@/services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
} from "@/utils/utils";

export const GROUP_VISIT_AVAILABLE_PATIENTS_QUERY_KEY =
  "group-visit-available-patients";

type Params = {
  groupVisitId?: string;
  pagination: DGT_Pagination;
  searchKey?: string;
  enabled?: boolean;
};

/**
 * Patients who can still be added to a group — those already enrolled are
 * excluded by the API.
 *
 * @author Rajkumar Rathod
 */
export const useGroupVisitAvailablePatients = <T>({
  groupVisitId,
  pagination,
  searchKey,
  enabled = true,
}: Params): useBaseContent<T> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);

  return useQuery({
    queryKey: [
      GROUP_VISIT_AVAILABLE_PATIENTS_QUERY_KEY,
      groupVisitId,
      pageIndex,
      pageSize,
      searchKey,
    ],
    enabled: enabled && !!groupVisitId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      new GroupVisitControllerApi(getConfigObject())
        .getAvailablePatients(
          groupVisitId as string,
          pageIndex, // page
          pageSize, // size
          searchKey ? searchKey : undefined, // searchString
          getXtenantId() // xTENANTID
        )
        .then((res) => res?.data),
  });
};
